import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { PaginatedResponse } from '../api';
import { Room } from '../community';
import { MemberInfo } from '../profile';
import {
  searchMembers,
  searchPolls,
  searchPosts,
  searchReplies,
  searchRooms,
  searchStories,
  searchStoriesComments,
} from './actions';
import { CommunitySearchResult } from './model/CommunitySearchResult';
import { PollSearchResult } from './model/PollSearchResult';
import { StorySearchResult } from './model/StorySearchResult';

export interface SearchState {
  searchMemberResults: PaginatedResponse<MemberInfo[]>;
  searchMemberLoading: boolean;
  searchRoomResults: PaginatedResponse<Room[]>;
  searchRoomLoading: boolean;
  searchPostsResults: PaginatedResponse<CommunitySearchResult[]>;
  searchPostsLoading: boolean;
  searchRepliesResults: PaginatedResponse<CommunitySearchResult[]>;
  searchRepliesLoading: boolean;
  searchPollsResults: PaginatedResponse<PollSearchResult[]>;
  searchPollsLoading: boolean;
  searchStoryResults: PaginatedResponse<StorySearchResult[]>;
  searchStoryCommentsResults: PaginatedResponse<StorySearchResult[]>;
  searchStoryLoading: boolean;
  searchStoryCommentsLoading: boolean;
  isSearchDrawerOpen: boolean;
}

const initialState: SearchState = {
  searchMemberResults: {
    data: [],
    xMsContinuationToken: undefined,
  },
  searchRoomResults: {
    data: [],
    xMsContinuationToken: undefined,
  },
  searchPostsResults: {
    data: [],
    xMsContinuationToken: undefined,
  },
  searchRepliesResults: {
    data: [],
    xMsContinuationToken: undefined,
  },
  searchPollsResults: {
    data: [],
    xMsContinuationToken: undefined,
  },
  searchStoryResults: {
    data: [],
    xMsContinuationToken: undefined,
  },
  searchStoryCommentsResults: {
    data: [],
    xMsContinuationToken: undefined,
  },
  isSearchDrawerOpen: false,
  searchPostsLoading: false,
  searchRepliesLoading: false,
  searchMemberLoading: false,
  searchRoomLoading: false,
  searchStoryLoading: false,
  searchStoryCommentsLoading: false,
  searchPollsLoading: false,
};

const profileSlice = createSlice({
  name: 'search',
  initialState,
  reducers: {
    clearSearchResults: (state) => {
      state.searchMemberResults = {
        data: [],
        xMsContinuationToken: undefined,
      };
      state.searchRoomResults = {
        data: [],
        xMsContinuationToken: undefined,
      };
      state.searchPollsResults = {
        data: [],
        xMsContinuationToken: undefined,
      };
      state.searchPostsResults = {
        data: [],
        xMsContinuationToken: undefined,
      };
      state.searchRepliesResults = {
        data: [],
        xMsContinuationToken: undefined,
      };
      state.searchStoryResults = {
        data: [],
        xMsContinuationToken: undefined,
      };
      state.searchStoryCommentsResults = {
        data: [],
        xMsContinuationToken: undefined,
      };
    },
    showSearchDrawer: (state) => {
      state.isSearchDrawerOpen = true;
    },
    hideSearchDrawer: (state) => {
      state.isSearchDrawerOpen = false;
    },
    removeMemberFromSearchResults: (state, action: PayloadAction<string>) => {
      state.searchMemberResults = {
        ...state.searchMemberResults,
        data: [
          ...state.searchMemberResults.data.filter(
            (member) => member.memberId !== action.payload
          ),
        ],
      };
    },
    setSearchMemberLoading: (state, action: PayloadAction<boolean>) => {
      state.searchMemberLoading = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(searchMembers.fulfilled, (state, action) => {
        if (action.meta.arg.xMsContinuationToken) {
          const tempSearchResults = state.searchMemberResults?.data || [];
          tempSearchResults?.push(...action.payload.data);
          state.searchMemberResults = {
            data: tempSearchResults,
            xMsContinuationToken: action.payload.xMsContinuationToken,
          };
        } else {
          state.searchMemberResults = {
            data: action.payload.data,
            xMsContinuationToken: action.payload.xMsContinuationToken,
          };
        }
        state.searchMemberLoading = false;
      })
      .addCase(searchMembers.rejected, (state) => {
        state.searchMemberResults.data = [];
        state.searchMemberLoading = false;
      })
      .addCase(searchMembers.pending, (state) => {
        state.searchMemberLoading = true;
      })
      .addCase(searchRooms.fulfilled, (state, action) => {
        if (action.meta.arg.xMsContinuationToken) {
          const tempSearchResults = state.searchRoomResults?.data || [];
          tempSearchResults?.push(...action.payload.data);
          state.searchRoomResults = {
            data: tempSearchResults,
            xMsContinuationToken: action.payload.xMsContinuationToken,
          };
        } else {
          state.searchRoomResults = {
            data: action.payload.data,
            xMsContinuationToken: action.payload.xMsContinuationToken,
          };
        }

        state.searchRoomLoading = false;
      })
      .addCase(searchRooms.rejected, (state) => {
        state.searchRoomResults.data = [];
        state.searchRoomLoading = false;
      })
      .addCase(searchRooms.pending, (state) => {
        state.searchRoomLoading = true;
      })
      .addCase(searchReplies.fulfilled, (state, action) => {
        if (action.meta.arg.xMsContinuationToken) {
          const tempSearchResults = state.searchRepliesResults?.data || [];
          tempSearchResults?.push(...action.payload.data);
          state.searchRepliesResults = {
            data: tempSearchResults,
            xMsContinuationToken: action.payload.xMsContinuationToken,
          };
        } else {
          state.searchRepliesResults = {
            data: action.payload.data,
            xMsContinuationToken: action.payload.xMsContinuationToken,
          };
        }

        state.searchRepliesLoading = false;
      })
      .addCase(searchReplies.rejected, (state) => {
        state.searchRepliesResults = {
          data: [],
          xMsContinuationToken: undefined,
        };
        state.searchRepliesLoading = false;
      })
      .addCase(searchReplies.pending, (state) => {
        state.searchRepliesLoading = true;
      })
      .addCase(searchPosts.fulfilled, (state, action) => {
        if (action.meta.arg.xMsContinuationToken) {
          const tempSearchResults = state.searchPostsResults?.data || [];
          tempSearchResults?.push(...action.payload.data);
          state.searchPostsResults = {
            data: tempSearchResults,
            xMsContinuationToken: action.payload.xMsContinuationToken,
          };
        } else {
          state.searchPostsResults = {
            data: action.payload.data,
            xMsContinuationToken: action.payload.xMsContinuationToken,
          };
        }

        state.searchPostsLoading = false;
      })
      .addCase(searchPosts.rejected, (state) => {
        state.searchPostsResults = {
          data: [],
          xMsContinuationToken: undefined,
        };
        state.searchPostsLoading = false;
      })
      .addCase(searchPosts.pending, (state) => {
        state.searchPostsLoading = true;
      })
      .addCase(searchPolls.fulfilled, (state, action) => {
        if (action.meta.arg.xMsContinuationToken) {
          const tempSearchResults = state.searchPollsResults?.data || [];
          tempSearchResults?.push(...action.payload.data);
          state.searchPollsResults = {
            data: tempSearchResults,
            xMsContinuationToken: action.payload.xMsContinuationToken,
          };
        } else {
          state.searchPollsResults = {
            data: action.payload.data,
            xMsContinuationToken: action.payload.xMsContinuationToken,
          };
        }

        state.searchPollsLoading = false;
      })
      .addCase(searchPolls.rejected, (state) => {
        state.searchPollsResults = {
          data: [],
          xMsContinuationToken: undefined,
        };
        state.searchPollsLoading = false;
      })
      .addCase(searchPolls.pending, (state) => {
        state.searchPollsLoading = true;
      })
      .addCase(searchStories.fulfilled, (state, action) => {
        if (action.meta.arg.xMsContinuationToken) {
          const tempSearchResults = state.searchStoryResults?.data ?? [];
          tempSearchResults?.push(...action.payload.data);
          state.searchStoryResults = {
            data: tempSearchResults,
            xMsContinuationToken: action.payload.xMsContinuationToken,
          };
        } else {
          state.searchStoryResults = {
            data: action.payload.data,
            xMsContinuationToken: action.payload.xMsContinuationToken,
          };
        }

        state.searchStoryLoading = false;
      })
      .addCase(searchStories.rejected, (state) => {
        state.searchStoryResults.data = [];
        state.searchStoryLoading = false;
      })
      .addCase(searchStories.pending, (state) => {
        state.searchStoryLoading = true;
      })
      .addCase(searchStoriesComments.fulfilled, (state, action) => {
        if (action.meta.arg.xMsContinuationToken) {
          const tempSearchResults =
            state.searchStoryCommentsResults?.data ?? [];
          tempSearchResults?.push(...action.payload.data);
          state.searchStoryCommentsResults = {
            data: tempSearchResults,
            xMsContinuationToken: action.payload.xMsContinuationToken,
          };
        } else {
          state.searchStoryCommentsResults = {
            data: action.payload.data,
            xMsContinuationToken: action.payload.xMsContinuationToken,
          };
        }
        state.searchStoryCommentsLoading = false;
      });
  },
});

export const {
  clearSearchResults,
  showSearchDrawer,
  hideSearchDrawer,
  removeMemberFromSearchResults,
  setSearchMemberLoading,
} = profileSlice.actions;

export default profileSlice.reducer;
